import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Container, Row } from 'reactstrap';
import { Grid, SvgIcon, SvgIconProps } from '@mui/material';
import { PageHeader, Section, SpinnerArea } from '../../../shared/components';
import Context from './context/DetailsContext';
import GrievanceTimeline from './Timeline/GrievanceTimeline';
import GrievanceSubtitle from './GrievanceSubtitle';
import UIKitButton from '../../../shared/UIKITComponents/Button';
import MainContext from '../../App/MainProvider/context';
import grievanceService from '../../../services/grievanceService';
import { GRIEVANCE_PHASES } from '../constants';
import GrievancePhases from './Phases/GrievancePhases';
import {
  GRIEVANCE_MILESTONES_CODES,
  GRIEVANCE_STATUSES,
  GrievanceLogbookItem,
  GrievancePhase,
  GrievancePhaseDataApply,
} from '../../../services/types/Grievance';

import GeneralInformationTabContainer from '../GeneralInformationTabContainer';
import Logbook from './Logbook';
import UIKitConfirmModal from '../../../shared/UIKITComponents/ConfirmModal';

// Único ícono de @mui/icons-material usado en toda la app - inlineado localmente
// (mismo path SVG que CreateOutlined.js del paquete real) para no depender de
// @mui/icons-material, cuyos ~2000 archivos hacían la instalación muy lenta/
// se colgaba en máquinas Windows con antivirus en tiempo real (Fase 4).
const CreateOutlinedIcon = (props: SvgIconProps) => (
  <SvgIcon {...props}>
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM5.92 19H5v-.92l9.06-9.06.92.92L5.92 19zM20.71 5.63l-2.34-2.34c-.2-.2-.45-.29-.71-.29s-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41z" />
  </SvgIcon>
);

const GrievanceDetails: React.FC = () => {
  const { service, notify, user, inSuperUserMode } = useContext(MainContext);
  const { grievanceNumber, grievance, grievancePhaseData, phases, reload, loading } =
    useContext(Context);
  const { t } = useTranslation();
  const location = useLocation();

  const querySrting = new URLSearchParams(location.search);
  const q = (key: string) => querySrting.get(key);
  const operationId = q('operationId') ? +(q('operationId') || 0) : undefined;

  const [saving, setSaving] = useState(false);
  const [showConfirmNextPhaseModal, setShowConfirmNextPhaseModal] = useState(false);
  const [showConfirmDiscontinueModal, setShowConfirmDiscontinueModal] = useState(false);

  const { canSeeLogbook, canUpdatePhaseOrMilestones } = useMemo(() => {
    const canUpdatePhaseOrMilestonesValue =
      inSuperUserMode() || user.grievanceRole === 'GPC' || user.grievanceRole === 'GPT';

    const canSeeLogbookValue =
      (inSuperUserMode() ||
        user.isHso ||
        user.grievanceRole === 'GPC' ||
        user.grievanceRole === 'GPT' ||
        user.isGrievanceGH) &&
      grievance.grievanceStatus === GRIEVANCE_STATUSES.IN_PROGRESS;
    return {
      canSeeLogbook: canSeeLogbookValue,
      canUpdatePhaseOrMilestones: canUpdatePhaseOrMilestonesValue,
    };
  }, [user, inSuperUserMode, grievance.grievanceStatus]);

  const { nextPhase, canMoveToNextPhase, canMoveToClosed } = useMemo(() => {
    const currentPhaseValue =
      grievancePhaseData &&
      phases &&
      phases.find((p) => p.phase === grievancePhaseData.currentPhaseName);
    const currentPhaseIndexValue =
      currentPhaseValue && phases
        ? phases.findIndex((x) => x.phase === currentPhaseValue.phase)
        : -1;
    const nextPhaseValue =
      currentPhaseIndexValue > -1 && phases && phases.length > currentPhaseIndexValue
        ? phases[currentPhaseIndexValue + 1]
        : undefined;

    const canMoveToNextPhaseValue =
      currentPhaseValue &&
      currentPhaseValue.phase !== GRIEVANCE_PHASES.CLOSED &&
      !currentPhaseValue.milestones.some((m) => m.isRequired && m.date === null);

    const applicabilityCompleted =
      phases &&
      phases.find(
        (p) =>
          p.phase === GRIEVANCE_PHASES.APPLICABILITY && p.milestones.every((m) => m.date !== null)
      );

    const canMoveToClosedValue =
      applicabilityCompleted &&
      currentPhaseValue &&
      currentPhaseValue.phase !== GRIEVANCE_PHASES.CLOSED;

    return {
      currentPhase: currentPhaseValue,
      currentPhaseIndex: currentPhaseIndexValue,
      nextPhase: nextPhaseValue,
      canMoveToNextPhase: canMoveToNextPhaseValue,
      canMoveToClosed: canMoveToClosedValue,
    };
  }, [grievancePhaseData, phases]);

  const discontinueManagement = useCallback(() => {
    if (!canMoveToClosed || !canUpdatePhaseOrMilestones) {
      return;
    }

    service(() => grievanceService.discontinueManagement(grievanceNumber), {
      callback: () => {
        notify(t('grievances'), t('grievanceDiscontinueSuccessfully'), 'success');
        reload();
      },
      loader: setSaving,
    });
  }, [canMoveToClosed, grievanceNumber, notify, reload, service, canUpdatePhaseOrMilestones]);

  const moveToNextPhase = useCallback(() => {
    if (!canMoveToNextPhase || !canUpdatePhaseOrMilestones) {
      return;
    }

    service(() => grievanceService.moveToNextPhase(grievanceNumber), {
      callback: () => {
        notify(t('grievances'), t('grievanceMovedToNextPhaseSuccessfully'), 'success');
        reload();
      },
      loader: setSaving,
    });
  }, [canMoveToNextPhase, grievanceNumber, notify, reload, service, canUpdatePhaseOrMilestones]);

  const onPhaseApply = useCallback(
    (grievancePhase: GrievancePhase, dataApply: GrievancePhaseDataApply) => {
      const canEditAuthClousureDate =
        (inSuperUserMode() || user.isHso) && grievancePhase.phase === GRIEVANCE_PHASES.CLOSED;
      if (!canUpdatePhaseOrMilestones && !canEditAuthClousureDate) {
        return;
      }

      service(() => grievanceService.phaseApply(grievanceNumber, grievancePhase, dataApply), {
        callback: () => {
          let messageSuccess = t('grievancePhaseAppliedSuccessfully');
          if (grievancePhase.phase === GRIEVANCE_PHASES.CLOSED) {
            const prevClousureNotification = phases
              .flatMap((x) => x.milestones)
              .find((m) => m.code === GRIEVANCE_MILESTONES_CODES.CLOSURE_NOTIFICATION);
            const currClousureNotification = grievancePhase.milestones.find(
              (m) => m.code === GRIEVANCE_MILESTONES_CODES.CLOSURE_NOTIFICATION
            );

            if (
              (!prevClousureNotification && currClousureNotification) ||
              (prevClousureNotification &&
                currClousureNotification &&
                prevClousureNotification.date !== currClousureNotification.date)
            ) {
              messageSuccess = t('grievanceClosedSuccessfully');
            }
          }

          notify(t('grievances'), messageSuccess, 'success');
          reload();
        },
        loader: setSaving,
      });
    },
    [grievanceNumber, notify, reload, service]
  );
  const canEdit =
    inSuperUserMode() ||
    user.grievanceRole === 'GPC' ||
    user.grievanceRole === 'GPT' ||
    user.isGrievanceGH;

  const onRemoveComment = useCallback(
    (commentId: number, messageSuccess?: string) => {
      service(() => grievanceService.removeComment(grievanceNumber, commentId), {
        callback: () => {
          notify(
            t('grievances'),
            messageSuccess || t('grievanceCommentRemovedSuccessfully'),
            'success'
          );
          reload();
        },
        loader: setSaving,
      });
    },
    [grievanceNumber, notify, reload, service]
  );

  const onSaveLogbookItem = useCallback(
    (logbookItem: GrievanceLogbookItem, messageSuccess?: string) => {
      service(() => grievanceService.saveLogbookItem(grievanceNumber, logbookItem), {
        callback: () => {
          notify(
            t('grievances'),
            messageSuccess || t('grievanceLogbookItemSavedSuccessfully'),
            'success'
          );
          reload();
        },
        loader: setSaving,
      });
    },
    [grievanceNumber, notify, reload, service]
  );

  const linkConvergence = grievanceNumber && canEdit && (
    <a
      // target="_blank"
      href={`../edit/${grievanceNumber}`}
      rel="noopener noreferrer"
      title={t('edit')}
      style={{ display: 'inline-flex', alignItems: 'center' }} // Opcional: ayuda a centrar el icono de MUI
    >
      <CreateOutlinedIcon fontSize="small" />
    </a>
  );

  useEffect(() => () => reload(), [inSuperUserMode]);

  const backUrl = operationId ? `/operations/${operationId}` : '/grievance-protocol';
  return (
    grievancePhaseData && (
      <Container>
        <PageHeader title={grievanceNumber} link={linkConvergence} backTimes={1} backUrl={backUrl}>
          <GrievanceSubtitle />
        </PageHeader>
        <Row id="grievanceOperationGeneralInformation">
          <Section>
            <GeneralInformationTabContainer grievance={grievance} loading={loading} />
          </Section>
        </Row>
        <Row>
          <Section title="Grievance Data">
            <SpinnerArea loading={loading || saving}>
              <div className="px-3">
                {phases && (
                  <div className="mb-3">
                    <Grid
                      container
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Grid>
                        <h5>{t('timeline')}</h5>
                      </Grid>
                      <Grid>
                        <Grid container spacing={1}>
                          {canUpdatePhaseOrMilestones &&
                            nextPhase &&
                            grievancePhaseData.currentStatus === GRIEVANCE_STATUSES.IN_PROGRESS && (
                              <Grid>
                                <UIKitButton
                                  size="small"
                                  text={t('moveToNextPhase', {
                                    phase: t(`grievanceStages.${nextPhase.phase}`),
                                  })}
                                  onClick={() => setShowConfirmNextPhaseModal(true)}
                                  disabled={!canMoveToNextPhase || !canUpdatePhaseOrMilestones}
                                />
                              </Grid>
                            )}
                          {/* Misma condición que "Move to next phase", incluido `nextPhase`: antes
                              de la migración a React 19 ambos botones compartían un único bloque, y
                              al separarlos esta condición perdió el `nextPhase &&`. En la fase Closed
                              (la última, sin fase siguiente) la queja puede seguir In Progress, pero
                              el botón no se debe mostrar. */}
                          {canUpdatePhaseOrMilestones &&
                            nextPhase &&
                            grievancePhaseData.currentStatus === GRIEVANCE_STATUSES.IN_PROGRESS && (
                              <Grid>
                                <UIKitButton
                                  size="small"
                                  variant="stroke"
                                  text={t('discontinueManagement')}
                                  onClick={() => setShowConfirmDiscontinueModal(true)}
                                  disabled={!canMoveToClosed || !canUpdatePhaseOrMilestones}
                                />
                              </Grid>
                            )}
                        </Grid>
                      </Grid>
                    </Grid>
                    <GrievanceTimeline />
                    {canSeeLogbook && <Logbook />}

                    <GrievancePhases
                      onPhaseApply={onPhaseApply}
                      onRemoveComment={onRemoveComment}
                      onSaveLogbookItem={onSaveLogbookItem}
                      isReadOnly={!canUpdatePhaseOrMilestones}
                    />
                  </div>
                )}
              </div>
            </SpinnerArea>
          </Section>
        </Row>

        <UIKitConfirmModal
          open={showConfirmNextPhaseModal}
          onClose={() => setShowConfirmNextPhaseModal(false)}
          onConfirm={moveToNextPhase}
          title={t('grievances')}
          message={
            nextPhase && nextPhase.phase === GRIEVANCE_PHASES.CLOSED
              ? t('grievanceConfirmingMoveToClosedMessage')
              : t('grievanceConfirmingMoveToNextPhaseMessage')
          }
        />

        <UIKitConfirmModal
          open={showConfirmDiscontinueModal}
          onClose={() => setShowConfirmDiscontinueModal(false)}
          onConfirm={discontinueManagement}
          title={t('grievances')}
          message={t('grievanceConfirmingDiscontinueMessage')}
        />
      </Container>
    )
  );
};

export default GrievanceDetails;
